import { call, put, select, takeLatest } from 'typed-redux-saga';
import { Action } from '@reduxjs/toolkit';
import { appConfigSelectors } from '../appConfig/appConfigSlice';
import { getAppAuthByTier } from '../../web-apis/cloud-backend-auth';
import { BackendTier } from '../auth/auth';
import {
    fetchRoiModelsStarted, fetchRoiModelsSucceeded, fetchRoiModelsFailed,
    generateRegexStarted, generateRegexSucceeded, generateRegexFailed,
    generateRoiOperationStarted, generateRoiOperationSucceeded, generateRoiOperationFailed,
    validateRoiOperationsStarted, validateRoiOperationsSucceeded, validateRoiOperationsFailed,
} from './assistantSlice';
import { generateRegex, generateRoiOperation, validateRoiOperations, fetchRoiModels } from '../../web-apis/portal-api';

/**
 * Resolves the assistant API tier from deployment configuration.
 * NOTE: This is a temporary mapping. The API tier parameter semantics will change
 * in the near future — keep this function isolated for easy replacement.
 *
 * Priority order: special cloud regions (research, demo, dev) take precedence
 * over the deployment tier, because these regions have their own model sets.
 * Falls back to 'dev' if nothing matches.
 */
function resolveAssistantApiTier(config: NonNullable<ReturnType<typeof appConfigSelectors.selectAppDeploymentInfo>>): string {
    // Special cloud regions override the deployment tier
    const region = config.cloudRegion?.toLowerCase();
    if (region === 'research') { return 'research'; }
    if (region === 'demo') { return 'demo'; }
    if (region === 'dev') { return 'dev'; }
    // Standard deployment tiers
    const tier = config.tier?.toLowerCase();
    if (tier === 'clinical') { return 'clinical'; }
    if (tier === 'nonclinical') { return 'nonclinical'; }
    return 'dev';
}

function getAssistantApiUrl(config: ReturnType<typeof appConfigSelectors.selectAppDeploymentInfo>): string {
    const url = config?.assistantApiUrl;
    if (!url) {
        throw new Error('Assistant API URL is not configured. Please set assistantApiUrl in config.json.');
    }
    return url;
}

function* fetchRoiModelsSaga() {
    try {
        const config = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        if (!config) {
            throw new Error('Deployment config is not available');
        }
        const baseUrl = getAssistantApiUrl(config);
        const appAuth = getAppAuthByTier(BackendTier.Default);
        const apiTier = resolveAssistantApiTier(config);
        const response = yield* call(fetchRoiModels, appAuth, baseUrl, { tier: apiTier });
        yield* put(fetchRoiModelsSucceeded(response.models));
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch ROI models';
        console.error(message);
        yield* put(fetchRoiModelsFailed(message));
    }
}

function* generateRegexSaga(action: Action) {
    if (!generateRegexStarted.match(action)) return;
    const { requestId } = action.payload;
    try {
        const config = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        const baseUrl = getAssistantApiUrl(config);
        const appAuth = getAppAuthByTier(BackendTier.Default);
        const response = yield* call(generateRegex, appAuth, baseUrl, {
            description: action.payload.description,
            match_examples: action.payload.matchExamples,
            non_match_examples: action.payload.nonMatchExamples,
        });
        yield* put(generateRegexSucceeded({ regex: response.regex, requestId }));
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to generate regex';
        console.error(message);
        yield* put(generateRegexFailed({ error: message, requestId }));
    }
}

function* generateRoiOperationSaga(action: Action) {
    if (!generateRoiOperationStarted.match(action)) return;
    const { requestId } = action.payload;
    try {
        const config = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        if (!config) {
            throw new Error('Deployment config is not available');
        }
        const baseUrl = getAssistantApiUrl(config);
        const appAuth = getAppAuthByTier(BackendTier.Default);
        const apiTier = resolveAssistantApiTier(config);
        const request: Parameters<typeof generateRoiOperation>[2] = {
            query: action.payload.query,
            model: action.payload.model,
            tier: apiTier,
        };
        if (action.payload.userThreadId) {
            request.user_thread_id = action.payload.userThreadId;
        }
        const response = yield* call(generateRoiOperation, appAuth, baseUrl, request);
        yield* put(generateRoiOperationSucceeded({
            operation: response.operation,
            validationStatus: response.validation_status,
            userThreadId: response.user_thread_id,
            requestId,
        }));
    } catch (error) {
        let message = error instanceof Error ? error.message : 'Failed to generate ROI operation';
        console.error(message);
        if (message.includes('Assistant run failed with status: failed')) {
            message = 'ASSISTANT_RUN_FAILED';
        }
        yield* put(generateRoiOperationFailed({ error: message, requestId }));
    }
}

function* validateRoiOperationsSaga(action: Action) {
    if (!validateRoiOperationsStarted.match(action)) return;
    try {
        const config = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        const baseUrl = getAssistantApiUrl(config);
        const appAuth = getAppAuthByTier(BackendTier.Default);
        const response = yield* call(validateRoiOperations, appAuth, baseUrl, {
            operations: action.payload.operations,
        });
        yield* put(validateRoiOperationsSucceeded(response.results));
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to validate ROI operations';
        console.error(message);
        yield* put(validateRoiOperationsFailed(message));
    }
}

export function getWatchesForAssistantSagas() {
    return [
        takeLatest(fetchRoiModelsStarted.type, fetchRoiModelsSaga),
        takeLatest(generateRegexStarted.type, generateRegexSaga),
        takeLatest(generateRoiOperationStarted.type, generateRoiOperationSaga),
        takeLatest(validateRoiOperationsStarted.type, validateRoiOperationsSaga),
    ];
}
